import { useEffect } from 'react';
import { canonicalPath } from '../utils/urls';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  author?: string;
  publishedTime?: string;
  structuredData?: object;
  breadcrumbs?: { name: string; url: string }[];
  robots?: string;
}

export default function SEO({
  title,
  description,
  keywords,
  image = 'https://equinord.dk/images/home-hero.jpg',
  url,
  type = 'website',
  author,
  publishedTime,
  structuredData,
  breadcrumbs,
  robots = 'index, follow'
}: SEOProps) {
  const siteTitle = 'EquiNord';
  const fullTitle = title.includes(siteTitle) ? title : `${title} | ${siteTitle}`;
  const currentUrl = url || canonicalPath();

  useEffect(() => {
    document.title = fullTitle;

    const updateMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);
    };

    updateMetaTag('description', description);
    updateMetaTag('robots', robots);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:image:alt', title, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:type', type === 'product' ? 'product' : type === 'article' ? 'article' : 'website', true);
    updateMetaTag('og:site_name', siteTitle, true);
    updateMetaTag('og:locale', 'da_DK', true);

    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);

    if (type === 'article' && author) {
      updateMetaTag('article:author', author, true);
    }
    if (type === 'article' && publishedTime) {
      updateMetaTag('article:published_time', publishedTime, true);
    }

    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = currentUrl;

    const pageSchemaId = 'page-structured-data';
    let scriptTag = document.getElementById(pageSchemaId) as HTMLScriptElement | null;
    if (structuredData) {
      const enrichedData = {
        ...structuredData as Record<string, unknown>,
        inLanguage: 'da',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': currentUrl
        }
      };

      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = pageSchemaId;
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(enrichedData);
    } else if (scriptTag) {
      scriptTag.remove();
    }

    const breadcrumbSchemaId = 'breadcrumb-structured-data';
    let breadcrumbScript = document.getElementById(breadcrumbSchemaId) as HTMLScriptElement | null;
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: crumb.url
        }))
      };

      if (!breadcrumbScript) {
        breadcrumbScript = document.createElement('script');
        breadcrumbScript.id = breadcrumbSchemaId;
        breadcrumbScript.type = 'application/ld+json';
        document.head.appendChild(breadcrumbScript);
      }
      breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    } else if (breadcrumbScript) {
      breadcrumbScript.remove();
    }

    return () => {
      const pageScript = document.getElementById(pageSchemaId);
      if (pageScript) pageScript.remove();
      const breadScript = document.getElementById(breadcrumbSchemaId);
      if (breadScript) breadScript.remove();
    };
  }, [fullTitle, description, keywords, image, currentUrl, type, author, publishedTime, structuredData, breadcrumbs, robots, title]);

  return null;
}
